import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../../utils/baseQuery";
import { components } from "../../types/api";

export type WIPCampaignMetrics = components["schemas"]["WIPCampaignMetrics"];
type WipMetricsResponse =
  components["schemas"]["ApiResponse_WIPCampaignMetrics-Array_"];
export type WipLineItemsResult = components["schemas"]["WipLineItemsResult"];
export type WipLineItem = components["schemas"]["WipLineItem"];
type WipLineItemsResponse =
  components["schemas"]["ApiResponse_WipLineItemsResult_"];
export type ManualMetricsValues = components["schemas"]["ManualMetricsValues"];
export type DirectBuyMetricsInput =
  components["schemas"]["DirectBuyMetricsInput"];
type DirectBuyMetricsResponse =
  components["schemas"]["ApiResponse_ManualMetricsValues_"];
export type WipDailySeries = components["schemas"]["WipDailySeries"];
export type WipDailySeriesPoint = components["schemas"]["WipDailySeriesPoint"];
export type WipVolumeMetric = components["schemas"]["WipVolumeMetric"];
type WipDailySeriesResponse =
  components["schemas"]["ApiResponse_WipDailySeries_"];

export interface WipMetricsQuery {
  month?: string;
  /** Solo las líneas de esa persona: todas si es partner success, las de su plataforma si lleva una. */
  assignedUserId?: string;
}

export const wipApi = createApi({
  reducerPath: "wipApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["WipMetrics"],
  endpoints: (builder) => ({
    getWipMetrics: builder.query<WipMetricsResponse, WipMetricsQuery>({
      query: ({ month, assignedUserId } = {}) => {
        const params = new URLSearchParams();
        if (month) params.set("month", month);
        if (assignedUserId) params.set("assignedUserId", assignedUserId);
        const qs = params.toString();
        return `/campaigns/wip-metrics${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["WipMetrics"],
    }),
    /** Line items de una línea de DV360 a nivel IO, con su presupuesto en vivo. */
    getWipLineItems: builder.query<WipLineItemsResponse, string>({
      query: (associatedCampaignId) =>
        `/campaigns/wip-metrics/line-items/${associatedCampaignId}`,
      providesTags: ["WipMetrics"],
    }),
    /** Serie diaria de una línea para las gráficas: volumen e inversión, reales e ideales. */
    getWipDailySeries: builder.query<WipDailySeriesResponse, string>({
      query: (associatedCampaignId) =>
        `/campaigns/wip-metrics/daily/${associatedCampaignId}`,
      providesTags: ["WipMetrics"],
    }),
    /** Guarda las métricas reales de una compra directa (línea sin API). */
    updateDirectBuyMetrics: builder.mutation<
      DirectBuyMetricsResponse,
      { associatedCampaignId: string; body: DirectBuyMetricsInput }
    >({
      query: ({ associatedCampaignId, body }) => ({
        url: `/campaigns/wip-metrics/direct-buys/${associatedCampaignId}`,
        method: "PUT",
        body,
      }),
      // El resto de columnas de la fila (pacing, costes unitarios) se recalculan
      // en el backend a partir de los valores manuales.
      invalidatesTags: ["WipMetrics"],
    }),
  }),
});

export const {
  useGetWipMetricsQuery,
  useGetWipLineItemsQuery,
  useGetWipDailySeriesQuery,
  useUpdateDirectBuyMetricsMutation,
} = wipApi;
